import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";

import { enquiryStatusOptions } from "Common/Components/StatusDropdown/statusOptions";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  deleteProductEnquiry,
  deleteServiceEnquiry,
  fetchProductEnquiries,
  fetchServiceEnquiries,
  handleProductEnquiryStatus,
  handleServiceEnquiryStatus,
} from "slices/enquiry/thunk";
import MasterListPage from "./MasterListPage";

import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { ENQ, ENQUIRY_TYPE } from "Constants/StatusConstant";
import FollowupModel from "components/FollowUpModal/FollowUpModel";
import StatusDropdown from "pages/Enquiry/Components/statusDropdown";
import StatusRemarkModal from "Common/Components/StatusRemarkModal/StatusRemarkModal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { showConfirm } from "helpers/swalHelper";
import DOMPurify from "dompurify";
import Tooltip from "@mui/material/Tooltip";
import Chip from "components/Chips/Chip";
import FollowUpPopUp from "components/FollowUpModal/FollowUpPopUp";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import { app_modules } from "helpers/apps_modules_list";
import { usePermission } from "hooks/usePermission";
import { clearAllTableStates } from "utils/clearTableStates";
import EnquiryDraftPage from "./EnquiryDraft";
import { Card } from "react-bootstrap";

interface EnquiryPageProps {
  type: "product" | "service";
  noWrapper?: boolean;
}

const EnquiryLogPage: React.FC<EnquiryPageProps> = ({ type, noWrapper = false }) => {
  document.title = type === "product" ? "Sales Enquiry" : "Service Enquiry";
  const navigate = useNavigate();
  const location = useLocation();
  /** On /tags-search/..., switch Log/Draft without route change (like Payment Management tabs) */
  const isTagsSearchRoute = location.pathname.startsWith("/tags-search");
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = React.useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  const tagsSearchEnquiryStorageKey =
    type === "product"
      ? "TagsSearchEnquiryTab_product"
      : "TagsSearchEnquiryTab_service";
  const [tagsEnquiryTab, setTagsEnquiryTab] = useState<
    "enquiry_log" | "draft"
  >(() => {
    const stored = sessionStorage?.getItem(tagsSearchEnquiryStorageKey);
    if (stored === "draft" || stored === "enquiry_log") return stored;
    return "enquiry_log";
  });
  const [reloadFlag, setReloadFlag] = useState(false);
  const [tagsTabSummary, setTagsTabSummary] = useState({
    loading: false,
    logCount: 0,
    draftCount: 0,
    hasError: false,
  });
  // Status Change Handle
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [remark, setRemark] = useState("");

  // Follow Up Modal  states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedFollowUpItem, setSelectedFollowUpItem] = useState<any>(null);

  const dispatch = useDispatch<any>();
  const activeType = type; // product by default
  const addButtonLabel =
    activeType === "product"
      ? "Create Sales Enquiry"
      : "Create Service Enquiry";

  const addRoute =
    activeType === "product"
      ? `/enquiry/add-product-enquiry`
      : `/enquiry/add-services-enquiry`;

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.enquiry?.app_name,
    activeType === "product"
      ? app_modules?.enquiry?.modules?.product
      : app_modules?.enquiry?.modules?.service
  );

  const handleView = (enquiryId: string) => {
    navigate(`/tags-search/enquiry/${type}/enquiry_log/view/${enquiryId}`);
  };

  const handleEdit = (enquiryId: string) => {
    navigate(`/enquiry/${type}/edit/${enquiryId}`);
  };

  const handleDelete = async (enquiryId: string) => {
    const result = await showConfirm(
      "This enquiry will be permanently deleted. Do you want to continue?",
      "Delete Enquiry",
      "Yes, delete",
      "Cancel"
    );

    if (!result.isConfirmed) return;
    try {
      if (activeType === "product") {
        await dispatch(deleteProductEnquiry(enquiryId)).unwrap();
      } else {
        await dispatch(deleteServiceEnquiry(enquiryId)).unwrap();
      }

      toast.success("Enquiry deleted successfully");
      setReloadFlag((prev) => !prev); // ✅ trigger re-fetch
    } catch (error: any) {
      toast.error(error || "Failed to delete enquiry");
    }
  };

  const openStatusModal = (itemId: string, status: string) => {
    setSelectedItemId(itemId);
    setSelectedStatus(status);
    setRemark("");
    setStatusModalOpen(true);
  };

  const handleStatusSubmit = async () => {
    if (!selectedItemId) return;

    await handleStatusChange(
      selectedItemId,
      selectedStatus,
      remark // 👈 pass remark
    );

    setStatusModalOpen(false);
  };

  const handleStatusChange = async (
    enquiryId: string,
    newStatus: string,
    remark: string
  ) => {
    try {
      if (activeType === "product") {
        await dispatch(
          handleProductEnquiryStatus({
            id: enquiryId,
            enquiry_stage: "enquiry_log",
            status: newStatus,
            remark,
          })
        ).unwrap();
      } else {
        await dispatch(
          handleServiceEnquiryStatus({
            id: enquiryId,
            enquiry_stage: "enquiry_log",
            status: newStatus,
            remark,
          })
        ).unwrap();
      }

      // toggle flag to trigger useEffect
      setReloadFlag((prev) => !prev);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "enquiry_number",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
      },
      ...(activeType === "service"
        ? [
          {
            header: "Service Number",
            accessorKey: "service_number",
            enableColumnFilter: false,
            enableSorting: false,
            filterType: "dropdown" as const,
            filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
              "service_number",
              activeType,
              ENQ.ENQUIRY_LOG
            )}`,
            cell: ({ row }: { row: any }) =>
              row.original?.service_number || "-",
          },
        ]
        : []),
      {
        header: "Enquiry Date",
        accessorKey: "enquiry_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.enquiry_date} />
        ),
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "customer_name",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.customer_name || "-",
      },
      {
        header: "Source of Enquiry",
        accessorKey: "source_of_enquiry",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "source_of_enquiry",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.source_of_enquiry || "-",
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "agent_name",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.agent_name || "-",
      },
      {
        header: "Reference",
        accessorKey: "reference_number",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "reference_number",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.reference_number || "-",
      },
      {
        header: "Status",
        accessorKey: "enquiry_status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "enquiry_status",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => {
          return (
            <StatusDropdown
              currentStatus={row.original.enquiry_status}
              itemId={
                activeType == "product"
                  ? row.original.enquiry_product_id
                  : row.original.enquiry_service_id
              }
              openStatusModal={openStatusModal}
              statusOptions={enquiryStatusOptions}
              disabled={false}
            />
          );
        },
      },
      {
        header: "Assigned To",
        accessorKey: "assigned_members",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "assigned_members",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,

        cell: ({ row }: { row: any }) => {
          const members = row.original?.assigned_members || [];

          if (!members.length) return "-";

          const names = members.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.quotation_date} />
        ),
      },
      {
        header: "Order Status",
        accessorKey: "order_status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "order_status",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => (
          <StatusChip status={row.original?.order_status} />
        ),
      },
      {
        header: "Last Follow-up Date",
        accessorKey: "follow_up_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return (
            <FormattedDate
              date={row.original?.enquiry_follow_ups?.[0]?.follow_up_date}
            />
          );
        },
      },
      {
        header: "Follow-up Remark",
        accessorKey: "follow_up_remarks",
        enableColumnFilter: false,
        enableSorting: false,

        size: 300, // 👈 fixed width
        minSize: 300,
        maxSize: 300,

        cell: ({ row }: { row: any }) => {
          const rawHtml =
            row.original?.enquiry_follow_ups?.[0]?.follow_up_remarks || "";

          if (!rawHtml) return <span>-</span>;

          const safeHtml = DOMPurify.sanitize(rawHtml);

          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <div
                  style={{
                    maxWidth: "450px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 500,
                    p: 1.5,
                  },
                },
              }}
            >
              <div
                style={{
                  width: "200px", // 👈 enforce inside cell
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  cursor: "pointer",
                }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </Tooltip>
          );
        },
      },

      {
        header: "Next Follow-up Date",
        accessorKey: "next_follow_up_date",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "date" as const,
        // filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN('company_name', activeType)}`,
        cell: ({ row }: { row: any }) => (
          <FormattedDate
            date={row.original?.enquiry_follow_ups?.[0]?.next_follow_up_date}
          />
        ),
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        filterType: "date" as const,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        filterType: "date" as const,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user.username}
            date={row.original.updated_at}
          />
        ),
      },
    ];

    const actionColumn = {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const enquiryId =
          activeType == "product"
            ? row.original.enquiry_product_id
            : row.original.enquiry_service_id;

        const data = row.original;

        const menuItems = canView || canUpdate ? [
          {
            icon: <VisibilityIcon fontSize="small" color="info" />,
            permission: canView,
            label: "View",
            onClick: () => handleView(enquiryId),
          },

          ...(row?.original?.enquiry_status?.toLowerCase() === "in progress"
            ? [
              {
                icon: <EditIcon fontSize="small" color="primary" />,
                label: "Edit",
                permission: canUpdate,
                onClick: () => handleEdit(enquiryId),
              },
            ]
            : []),

          {
            icon: <PersonAddIcon fontSize="small" color="success" />,
            label: "Follow Up",
            permission: canUpdate,
            // onClick: () => handleFollowUp(enquiryId),
            onClick: (item: any) => {
              setSelectedFollowUpItem(data);
              setShowFollowUpModal(true);
            },
          },

          ...(row?.original?.enquiry_status?.toLowerCase() !== "converted"
            ? [
              {
                label: "Delete",
                icon: <DeleteOutlineIcon fontSize="small" color="error" />,
                permission: canUpdate,
                onClick: () => handleDelete(enquiryId),
                className: "text-danger",
              },
            ]
            : []),
          // {
          //   icon: <DescriptionIcon fontSize="small" color="warning" />,
          //   label: "Create Quotation",
          //   onClick: () => handleNavigateToQuotation(enquiryId),
          // },
          // {
          //   icon: <DeleteOutlineIcon fontSize="small" color="error" />,
          //   label: "Junk",
          //   onClick: () => handleConvertJunk(enquiry_product_id),
          // },
        ] : [];

        return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
      },
    };

    return [...commonColumns, actionColumn];
  };

  useEffect(() => {
    return () => {
      if (!window.location.pathname.startsWith("/tags-search")) {
        sessionStorage.removeItem("TagsSearchEnquiryTab_product");
        sessionStorage.removeItem("TagsSearchEnquiryTab_service");
      }
    };
  }, []);

  useEffect(() => {
    if (!isTagsSearchRoute || !noWrapper || !tagFromUrl) return;

    let mounted = true;
    setTagsTabSummary((prev) => ({
      ...prev,
      loading: true,
      hasError: false,
    }));

    const fetchTagsSummary = async () => {
      try {
        const logStage = ENQ.ENQUIRY_LOG;
        const draftStage = "draft";

        const [logRes, draftRes] =
          activeType === "product"
            ? await Promise.all([
                dispatch(
                  fetchProductEnquiries({
                    page: 1,
                    pageSize: 1,
                    sort: "",
                    order: "",
                    search: tagFromUrl,
                    type: "tag",
                    filters: [],
                    stage: logStage,
                  }),
                ).unwrap(),
                dispatch(
                  fetchProductEnquiries({
                    page: 1,
                    pageSize: 1,
                    sort: "",
                    order: "",
                    search: tagFromUrl,
                    type: "tag",
                    filters: [],
                    stage: draftStage,
                  }),
                ).unwrap(),
              ])
            : await Promise.all([
                dispatch(
                  fetchServiceEnquiries({
                    page: 1,
                    pageSize: 1,
                    sort: "",
                    order: "",
                    search: tagFromUrl,
                    type: "tag",
                    filters: [],
                    stage: logStage,
                  }),
                ).unwrap(),
                dispatch(
                  fetchServiceEnquiries({
                    page: 1,
                    pageSize: 1,
                    sort: "",
                    order: "",
                    search: tagFromUrl,
                    type: "tag",
                    filters: [],
                    stage: draftStage,
                  }),
                ).unwrap(),
              ]);

        if (!mounted) return;
        setTagsTabSummary({
          loading: false,
          logCount: logRes?.rowCount ?? 0,
          draftCount: draftRes?.rowCount ?? 0,
          hasError: false,
        });
      } catch {
        if (!mounted) return;
        setTagsTabSummary({
          loading: false,
          logCount: 0,
          draftCount: 0,
          hasError: true,
        });
      }
    };

    fetchTagsSummary();
    return () => {
      mounted = false;
    };
  }, [activeType, dispatch, isTagsSearchRoute, noWrapper, tagFromUrl, reloadFlag]);

  const hideEntireTagsSection =
    isTagsSearchRoute &&
    noWrapper &&
    !tagsTabSummary.loading &&
    !tagsTabSummary.hasError &&
    tagsTabSummary.logCount === 0 &&
    tagsTabSummary.draftCount === 0;

  if (hideEntireTagsSection) {
    return null;
  }
  const forceShowOnTagsSearch = isTagsSearchRoute && noWrapper && !hideEntireTagsSection;

  const followUpModal = (
    <FollowUpPopUp
      open={showFollowUpModal}
      onClose={() => setShowFollowUpModal(false)}
      title="Follow-up and Activities"
    >
      {selectedFollowUpItem && (
        <FollowupModel
          data={selectedFollowUpItem}
          activeType={
            activeType === "product"
              ? ENQUIRY_TYPE?.PRODUCT
              : ENQUIRY_TYPE?.SERVICE
          }
          onClose={() => setShowFollowUpModal(false)}
        />
      )}
    </FollowUpPopUp>
  );

  if (isTagsSearchRoute) {
    return (
      <>
        {tagsEnquiryTab === "enquiry_log" && (
          <MasterListPage
            activeTab="enquiry_log"
            title={`${activeType === "product" ? "Sales" : "Service"} Enquiry`}
            addButtonLabel={addButtonLabel}
            addRoute={addRoute}
            columns={getColumns()}
            statusToggleUrl={`/enquiry/${activeType}`}
            activeType={activeType}
            reloadFlag={reloadFlag}
            noWrapper={noWrapper}
            onTagsSearchTabNavigate={(v) => {
              clearAllTableStates();
              const next =
                v === "draft" ? "draft" : "enquiry_log";
              setTagsEnquiryTab(next);
              sessionStorage.setItem(tagsSearchEnquiryStorageKey, next);
            }}
            forceShowOnTagsSearch={forceShowOnTagsSearch}
          />
        )}
        {tagsEnquiryTab === "draft" && (
          <EnquiryDraftPage
            type={type}
            noWrapper={noWrapper}
            forceShowOnTagsSearch={forceShowOnTagsSearch}
            onTagsSearchTabNavigate={(v) => {
              clearAllTableStates();
              const next =
                v === "draft" ? "draft" : "enquiry_log";
              setTagsEnquiryTab(next);
              sessionStorage.setItem(tagsSearchEnquiryStorageKey, next);
            }}
          />
        )}
        {followUpModal}
      </>
    );
  }

  return (
    <>
      <Card.Body className="mt-5">
        <MasterListPage
          activeTab="enquiry_log"
          title={`${activeType === "product" ? "Sales" : "Service"} Enquiry`}
          addButtonLabel={addButtonLabel}
          addRoute={addRoute}
          columns={getColumns()}
          statusToggleUrl={`/enquiry/${activeType}`}
          activeType={activeType}
          reloadFlag={reloadFlag}
          noWrapper={noWrapper}
        />
      </Card.Body>
      {followUpModal}

      {/* Remark Pop Up */}
      {/* <StatusRemarkModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSubmit={handleStatusSubmit}
        remark={remark}
        onRemarkChange={setRemark}
      /> */}
    </>
  );
};

export default EnquiryLogPage;
